import { inject, Injectable, signal } from "@angular/core";
import * as Sentry from "@sentry/angular";

import { PermissionDeniedError } from "@intocare/auth-data";
import { MustEnableMFAError, MustSetMainFunctionError, SiteNotFoundError, UnknownProblemError } from "@intocare/aws";
import { Address, DASupportInfo } from "@intocare/data-access/shared/organisation";
import { ENVIRONMENT_TOKEN, EnvironmentService } from "@intocare/environments";
import {
	Context,
	ContextNotInitializedError,
	ContextServiceInterface,
	ContextUser,
	FederatedEntity,
	FetchContextParameters,
	Problem,
	SiteAccessInformation,
	SiteOrganisationInformation,
	SupportedLocale,
} from "@intocare/util";

@Injectable({
	providedIn: "root",
})
export class ContextService implements ContextServiceInterface {
	private readonly environment = inject(ENVIRONMENT_TOKEN);
	private readonly environmentService = inject(EnvironmentService);

	private readonly _context = signal<Context>(defaultContext);
	public readonly context = this._context.asReadonly();

	public clearContext(): void {
		this._context.set(defaultContext);
	}

	private async fetchAccessInformation(token: string, home: string): Promise<SiteAccessInformation> {
		return fetch(`${this.environmentService.getBaseUrlForContext("access")}/information`, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({
				home,
			}),
		})
			.then((response) => {
				if (!response.ok) {
					Sentry.captureException(
						new Error(`Access information call failed with status ${response.status}: ${JSON.stringify(response)}`),
					);
					throw new Error("Failed to fetch access information");
				}

				return response.json() as Promise<SiteAccessInformation>;
			})
			.catch((error) => {
				Sentry.captureException(`Access information call failed: ${JSON.stringify(error)}`);
				throw error;
			});
	}

	private async fetchOrganisationInformation(parameters: {
		token: string;
		currentHome: string;
		requestedSites: string[];
		userLocale: SupportedLocale;
	}): Promise<SiteOrganisationInformation> {
		const { token, currentHome, requestedSites, userLocale } = parameters;
		const applicationName = "portal";

		return fetch(`${this.environmentService.getBaseUrlForContext("organisation")}/information`, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Accept-Language": userLocale,
				"X-Application": `${applicationName}/${this.environment.VERSION}`,
				"X-Api-Key": this.environment.API.key,
			},
			body: JSON.stringify({
				currentHome,
				requestedSites,
			}),
		})
			.then((response) => {
				if (!response.ok) {
					Sentry.captureException(
						new Error(
							`Organisation information call failed with status ${response.status}: ${JSON.stringify(response)}`,
						),
					);

					if (response.status === 400) {
						return response.json().then((errorResponse) => {
							if (errorResponse.code === "site_not_found") {
								throw new SiteNotFoundError();
							}

							throw new Error("Failed to fetch organisation information");
						});
					}

					throw new Error("Failed to fetch organisation information");
				}

				return response.json() as Promise<SiteOrganisationInformation>;
			})
			.catch((error) => {
				Sentry.captureException(`Organisation information call failed: ${JSON.stringify(error)}`);
				throw error;
			});
	}

	private async _fetchContext(token: string, hq: string, home: string): Promise<Context | undefined> {
		const contextInformation = await this.fetchAccessInformation(token, home);

		if (!contextInformation.active) {
			throw new Error("User is not active");
		}

		if ("problem" in contextInformation) {
			if (contextInformation.problem === Problem.MUST_ENABLE_MFA) {
				throw new MustEnableMFAError();
			}

			if (contextInformation.problem === Problem.MUST_SET_MAIN_FUNCTION) {
				throw new MustSetMainFunctionError();
			}

			throw new UnknownProblemError(`Unknown problem encountered: ${contextInformation.problem}`);
		}

		const { acceptedTermsAndConditions, permissions, roles, user, userLocale, accessibleSites, mainFunction } =
			contextInformation;

		if (!permissions.length) {
			throw new PermissionDeniedError("No permissions found");
		}

		const organisationInformation = await this.fetchOrganisationInformation({
			token,
			currentHome: home,
			requestedSites: accessibleSites.map((site) => site.id),
			userLocale,
		});

		const { timezone, supportedLocales, supportInfo, modules, currentSite, requestedSites } = organisationInformation;

		return {
			hq,
			user,
			supportedLocales,
			userLocale,
			acceptedTermsAndConditions,
			permissions,
			roles,
			accessibleSites: requestedSites,
			timezone,
			supportInfo,
			modules,
			currentSite,
			mainFunction,
		};
	}

	public async fetchContext(parameters: FetchContextParameters): Promise<Context> {
		const { token, hq, home } = parameters;

		return this._fetchContext(token, hq, home)
			.then((context) => {
				this._context.set(context);

				return context;
			})
			.catch((error) => {
				this.clearContext();
				Sentry.captureException(error);
				throw error;
			});
	}
}

const defaultContext: Context = Object.freeze({
	get hq(): string {
		throw new ContextNotInitializedError();
	},
	get user(): ContextUser {
		throw new ContextNotInitializedError();
	},
	get supportedLocales(): SupportedLocale[] {
		throw new ContextNotInitializedError();
	},
	get userLocale(): SupportedLocale {
		throw new ContextNotInitializedError();
	},
	get acceptedTermsAndConditions(): boolean {
		throw new ContextNotInitializedError();
	},
	get permissions(): string[] {
		throw new ContextNotInitializedError();
	},
	get roles(): { id: string }[] {
		throw new ContextNotInitializedError();
	},
	get accessibleSites(): {
		id: string;
		home: string;
		name: string;
	}[] {
		throw new ContextNotInitializedError();
	},
	get timezone(): "Europe/Brussels" {
		throw new ContextNotInitializedError();
	},
	get supportInfo(): DASupportInfo {
		throw new ContextNotInitializedError();
	},
	get modules(): {
		CallAlarms: boolean;
		Medication: boolean;
		OnboardResidents: boolean;
		RestraintsOverview: boolean;
		Dar: boolean;
		DietsV2: boolean;
		BelRAI: boolean;
		ManageDepartmentsAndRooms: boolean;
		ResidentApiSync: boolean;
		AllowMedicationGroupOverride: boolean;
		ValidationByHeadNurse: boolean;
	} {
		throw new ContextNotInitializedError();
	},
	get currentSite():
		| {
				id: string;
				home: string;
				name: string;
				address: Address;
				federatedEntity: FederatedEntity;
		  }
		| undefined {
		throw new ContextNotInitializedError();
	},
});
